import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Upload, 
  Check, 
  AlertCircle, 
  RefreshCw,
  FileText,
  Key,
  Building2,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { afipApi } from '../../lib/api';

export default function AFIPConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Form states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [certPassword, setCertPassword] = useState('');
  
  // Edit config
  const [editMode, setEditMode] = useState(false);
  const [editConfig, setEditConfig] = useState({});

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await afipApi.getConfig();
      setConfig(res.data);
    } catch (err) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const res = await afipApi.testConexion();
      setTestResult({ success: true, data: res.data });
      toast.success('Conexión exitosa con AFIP');
    } catch (err) {
      setTestResult({ 
        success: false, 
        error: err.response?.data?.detail || 'Error de conexión'
      });
      toast.error('Error al conectar con AFIP');
    } finally {
      setTesting(false);
    }
  };

  const handleUploadCert = async () => {
    if (!certFile || !certPassword) {
      toast.error('Complete todos los campos');
      return;
    }
    
    setUploading(true);
    
    try {
      await afipApi.uploadCertificado(certFile, certPassword);
      toast.success('Certificado cargado correctamente');
      setShowUploadDialog(false);
      setCertFile(null);
      setCertPassword('');
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar certificado');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await afipApi.updateConfig(editConfig);
      toast.success('Configuración actualizada');
      setEditMode(false);
      fetchConfig();
    } catch (err) {
      toast.error('Error al guardar configuración');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Facturación Electrónica AFIP</h1>
          <p className="text-slate-500">Configuración para emitir facturas electrónicas</p>
        </div>
        <a 
          href="https://auth.afip.gob.ar/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
        >
          Ir a AFIP <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Estado de conexión */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Estado de Conexión
          </CardTitle>
          <CardDescription>
            Verifica la conexión con los servicios de AFIP
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full ${config?.certificado_cargado ? 'bg-green-500' : 'bg-red-500'}`} />
              <div>
                <p className="font-medium text-slate-900">Certificado Digital</p>
                <p className="text-sm text-slate-500">
                  {config?.certificado_cargado ? 'Cargado' : 'No configurado'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full ${config?.password_configurado ? 'bg-green-500' : 'bg-red-500'}`} />
              <div>
                <p className="font-medium text-slate-900">Password</p>
                <p className="text-sm text-slate-500">
                  {config?.password_configurado ? 'Configurado' : 'No configurado'}
                </p>
              </div>
            </div>
          </div>

          {/* Info del certificado cargado */}
          {config?.certificado_info && !config.certificado_info.error && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="font-medium text-blue-800 mb-2">Información del Certificado</p>
              <div className="text-sm text-blue-700 space-y-1 font-mono">
                <p><span className="text-blue-500">Subject:</span> {config.certificado_info.subject}</p>
                <p><span className="text-blue-500">Issuer:</span> {config.certificado_info.issuer}</p>
                <p><span className="text-blue-500">Válido desde:</span> {config.certificado_info.valid_from}</p>
                <p><span className="text-blue-500">Válido hasta:</span> {config.certificado_info.valid_until}</p>
              </div>
              {config.certificado_info.issuer?.includes('AC=AFIP') && (
                <p className="mt-2 text-xs text-blue-600">
                  ⚠️ Si el Issuer contiene "TESTING" o "HOMOLOGACION", es un certificado de pruebas.
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button 
              onClick={handleTestConnection} 
              disabled={testing || !config?.certificado_cargado}
              className="gap-2"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Probar Conexión
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => setShowUploadDialog(true)}
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              {config?.certificado_cargado ? 'Cambiar Certificado' : 'Cargar Certificado'}
            </Button>
          </div>

          {/* Resultado del test */}
          {testResult && (
            <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-3">
                {testResult.success ? (
                  <Check className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.success ? 'Conexión Exitosa' : 'Error de Conexión'}
                  </p>
                  {testResult.success ? (
                    <div className="mt-2 text-sm text-green-700 space-y-1">
                      <p>Ambiente: <strong>{testResult.data.ambiente}</strong></p>
                      <p>CUIT: <strong>{testResult.data.cuit}</strong></p>
                      <p>Punto de Venta: <strong>{testResult.data.punto_venta}</strong></p>
                      <p>Último Comprobante FA: <strong>#{testResult.data.ultimo_comprobante_factura_a}</strong></p>
                      <p>Próximo Número: <strong>#{testResult.data.proximo_numero}</strong></p>
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-red-700">{testResult.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configuración */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Datos de Facturación
              </CardTitle>
              <CardDescription>
                Configuración del emisor de facturas
              </CardDescription>
            </div>
            {!editMode && (
              <Button variant="outline" size="sm" onClick={() => {
                setEditConfig({
                  cuit: config?.cuit,
                  punto_venta: config?.punto_venta,
                  production: config?.production
                });
                setEditMode(true);
              }}>
                Editar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CUIT Emisor</Label>
                  <Input 
                    value={editConfig.cuit || ''} 
                    onChange={(e) => setEditConfig({...editConfig, cuit: e.target.value})}
                    placeholder="30123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Punto de Venta</Label>
                  <Input 
                    type="number"
                    value={editConfig.punto_venta || ''} 
                    onChange={(e) => setEditConfig({...editConfig, punto_venta: parseInt(e.target.value)})}
                    placeholder="4"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <Label className="text-amber-800">Modo Producción</Label>
                  <p className="text-sm text-amber-600">
                    {editConfig.production 
                      ? 'Las facturas emitidas tienen validez fiscal' 
                      : 'Ambiente de pruebas (homologación)'}
                  </p>
                </div>
                <Switch 
                  checked={editConfig.production || false}
                  onCheckedChange={(checked) => setEditConfig({...editConfig, production: checked})}
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleSaveConfig}>Guardar</Button>
                <Button variant="outline" onClick={() => setEditMode(false)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-500">CUIT Emisor</p>
                <p className="text-lg font-mono font-medium">{config?.cuit || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Punto de Venta</p>
                <p className="text-lg font-mono font-medium">{config?.punto_venta || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Ambiente</p>
                <span className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium ${
                  config?.production 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {config?.production ? 'Producción' : 'Homologación (Testing)'}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instrucciones */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Guía de Configuración
          </CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <ol className="space-y-3 text-slate-600">
            <li>
              <strong>Obtener certificado digital:</strong> Ingresá a AFIP con Clave Fiscal y generá 
              un certificado digital en "Administración de Certificados Digitales".
            </li>
            <li>
              <strong>Habilitar servicio WSFE:</strong> En "Administrador de Relaciones de Clave Fiscal", 
              agregá el servicio "WSFE - Factura Electrónica" y asocialo al certificado.
            </li>
            <li>
              <strong>Punto de Venta:</strong> Asegurate de tener un punto de venta tipo RECE 
              habilitado en "ABM Puntos de Venta".
            </li>
            <li>
              <strong>Cargar certificado:</strong> Subí el archivo .p12 y su contraseña usando 
              el botón "Cargar Certificado".
            </li>
            <li>
              <strong>Probar conexión:</strong> Verificá que todo funciona con el botón 
              "Probar Conexión" antes de emitir facturas reales.
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Dialog para subir certificado */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Cargar Certificado AFIP
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Archivo .p12</Label>
              <Input 
                type="file" 
                accept=".p12"
                onChange={(e) => setCertFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-slate-500">
                El archivo .p12 se genera desde AFIP al crear el certificado digital
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Contraseña del certificado</Label>
              <Input 
                type="password"
                value={certPassword}
                onChange={(e) => setCertPassword(e.target.value)}
                placeholder="Ingresá la contraseña"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUploadCert} disabled={uploading || !certFile || !certPassword}>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cargando...
                </>
              ) : (
                'Cargar Certificado'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
